import { Component, inject, ChangeDetectorRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CrmService } from '../../shared/services/crm';
import { AffiliateTrackingService } from '../../core/services/affiliate-tracking.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-senior-independence',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-screen bg-[#f9f8f6] text-[#0e1726] font-sans antialiased selection:bg-[#c5a880] selection:text-white">

      <!-- ========================================== -->
      <!-- SECTION 1: HERO (Sovereign Living)         -->
      <!-- ========================================== -->
      <section class="relative pt-36 pb-24 lg:pt-44 lg:pb-36 px-6 sm:px-12 lg:px-24 bg-[#0e1726] text-white overflow-hidden">
        <!-- Subtle Ambient Background Accents -->
        <div class="absolute top-0 right-0 w-96 h-96 bg-[#c5a880]/10 rounded-full blur-3xl pointer-events-none"></div>
        <div class="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#c5a880]/5 rounded-full blur-3xl pointer-events-none"></div>

        <div class="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10">
          <!-- Text Column -->
          <div class="lg:col-span-7 space-y-8">
            <!-- Eyebrow Badge -->
            <div class="inline-flex items-center gap-2.5 px-4 py-2 bg-[#c5a880]/10 border border-[#c5a880]/40 rounded-full shadow-sm">
              <span class="w-2 h-2 rounded-full bg-[#c5a880] animate-pulse"></span>
              <span class="font-sans font-semibold text-xs tracking-[0.25em] text-[#c5a880] uppercase">
                DISCREET WHITE-GLOVE RESIDENTIAL CARE
              </span>
            </div>

            <!-- Headline -->
            <h1 class="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-normal leading-[1.15] tracking-tight">
              Preserving the Sanctity of Home.<br />
              <span class="italic text-[#c5a880] font-light">Elevating Your Autonomy.</span>
            </h1>

            <!-- Sub-headline -->
            <p class="font-sans text-lg sm:text-xl text-stone-300 font-light leading-relaxed max-w-2xl">
              True independence is not about doing everything yourself—it is having the freedom to enjoy your home without the physical strain of maintaining it. Our CPR-certified specialists take on high-risk domestic labor so you can live safely, beautifully, and on your own terms.
            </p>

            <!-- CTA Actions -->
            <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-5 pt-4">
              <a 
                routerLink="/book" [queryParams]="{ type: 'senior', track: 'walkthrough' }"
                class="bg-[#c5a880] hover:bg-[#b5976f] text-[#0e1726] font-sans font-bold h-14 px-8 rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 flex items-center justify-center gap-3 cursor-pointer text-base">
                <span>Book In-Home Walkthrough</span>
                <span class="material-symbols-outlined text-xl">calendar_month</span>
              </a>

              <a 
                routerLink="/book" [queryParams]="{ type: 'senior', track: 'estimate' }"
                class="border border-[#c5a880]/50 hover:border-[#c5a880] text-white hover:bg-[#c5a880]/10 font-sans font-medium h-14 px-8 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer text-base">
                <span class="material-symbols-outlined text-xl text-[#c5a880]">calculate</span>
                <span>Get Instant Remote Estimate</span>
              </a>
            </div>

            <!-- Trust Verification Badges -->
            <div class="pt-8 border-t border-stone-800/80 flex flex-wrap items-center gap-8 text-xs text-stone-400 font-sans uppercase tracking-widest">
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-[#c5a880] text-lg">medical_services</span>
                <span>CPR & First Aid Certified</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-[#c5a880] text-lg">shield</span>
                <span>Fully Insured & Vetted</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-[#c5a880] text-lg">location_on</span>
                <span>Manhattan & NYC Area</span>
              </div>
            </div>
          </div>

          <!-- Hero Editorial Image Column -->
          <div class="lg:col-span-5 relative">
            <div class="relative mx-auto max-w-md lg:max-w-none aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl border border-[#c5a880]/20">
              <img 
                src="/assets/img/cleaning-service-interior.png" 
                alt="Luxury NYC Residence Care" 
                class="w-full h-full object-cover"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-[#0e1726] via-transparent to-transparent opacity-60"></div>
              
              <!-- Floating Card Overlay -->
              <div class="absolute bottom-6 left-6 right-6 bg-[#0e1726]/90 backdrop-blur-md p-6 rounded-xl border border-[#c5a880]/30 text-white shadow-xl">
                <div class="flex items-start gap-4">
                  <div class="w-12 h-12 rounded-full bg-[#c5a880]/20 border border-[#c5a880]/40 flex items-center justify-center shrink-0">
                    <span class="material-symbols-outlined text-[#c5a880]">workspace_premium</span>
                  </div>
                  <div>
                    <h4 class="font-serif text-lg font-normal text-white">White-Glove Standard</h4>
                    <p class="font-sans text-xs text-stone-300 mt-1 leading-relaxed">
                      Customized home care protocols for Upper East Side, Tribeca, and Central Park residences.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ========================================== -->
      <!-- SECTION 2: THE DATA (The Hidden Risk)      -->
      <!-- ========================================== -->
      <section class="py-24 px-6 sm:px-12 lg:px-24 bg-[#f9f8f6] relative">
        <div class="max-w-7xl mx-auto">
          <!-- Section Header -->
          <div class="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span class="font-sans font-bold text-xs tracking-[0.25em] text-[#c5a880] uppercase">PREVENTATIVE VITALITY & RISK MANAGEMENT</span>
            <h2 class="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0e1726] font-normal">
              The Hidden Risk of Routine Maintenance
            </h2>
            <p class="font-sans text-base sm:text-lg text-stone-600 font-light leading-relaxed">
              Preserving vitality begins with mitigating unnecessary physical hazards. Professional support allows residents to focus their energy on living, not heavy physical labor.
            </p>
          </div>

          <!-- 3-Column Minimalist Data Cards -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <!-- Card 1 -->
            <div class="bg-white p-10 rounded-xl border border-[#c5a880]/30 shadow-sm hover:shadow-md hover:border-[#c5a880]/60 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div class="font-serif text-5xl lg:text-6xl text-[#0e1726] font-normal mb-2 tracking-tight">
                  60%
                </div>
                <h3 class="font-sans font-semibold text-lg text-[#0e1726] mb-4">
                  Of Falls Occur at Home
                </h3>
                <p class="font-sans text-stone-600 text-sm leading-relaxed font-light mb-6">
                  According to the National Institute on Aging, ordinary domestic tasks—like bending to scrub showers or reaching to dust high fixtures—represent the single greatest hazard to continuing independence.
                </p>
              </div>
              <div class="pt-4 border-t border-stone-100 flex items-center gap-2 text-xs text-stone-400 font-mono">
                <span class="material-symbols-outlined text-sm text-[#c5a880]">menu_book</span>
                <span>Source: National Institute on Aging (NIA)</span>
              </div>
            </div>

            <!-- Card 2 -->
            <div class="bg-white p-10 rounded-xl border border-[#c5a880]/30 shadow-sm hover:shadow-md hover:border-[#c5a880]/60 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div class="font-serif text-5xl lg:text-6xl text-[#0e1726] font-normal mb-2 tracking-tight">
                  1 in 4
                </div>
                <h3 class="font-sans font-semibold text-lg text-[#0e1726] mb-4">
                  Seniors Injured Annually
                </h3>
                <p class="font-sans text-stone-600 text-sm leading-relaxed font-light mb-6">
                  The CDC reports that falls are the primary cause of preventable injury for adults 65+. Eliminating slick flooring and ladder use drastically reduces domestic risk.
                </p>
              </div>
              <div class="pt-4 border-t border-stone-100 flex items-center gap-2 text-xs text-stone-400 font-mono">
                <span class="material-symbols-outlined text-sm text-[#c5a880]">menu_book</span>
                <span>Source: Centers for Disease Control and Prevention (CDC)</span>
              </div>
            </div>

            <!-- Card 3 -->
            <div class="bg-white p-10 rounded-xl border border-[#c5a880]/30 shadow-sm hover:shadow-md hover:border-[#c5a880]/60 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div class="font-serif text-5xl lg:text-6xl text-[#0e1726] font-normal mb-2 tracking-tight">
                  Zero Friction
                </div>
                <h3 class="font-sans font-semibold text-lg text-[#0e1726] mb-4">
                  Preserving Autonomy
                </h3>
                <p class="font-sans text-stone-600 text-sm leading-relaxed font-light mb-6">
                  Delegating rigorous physical upkeep allows residents to maintain full ownership of their daily routines while protecting vital mobility and health.
                </p>
              </div>
              <div class="pt-4 border-t border-stone-100 flex items-center gap-2 text-xs text-stone-400 font-mono">
                <span class="material-symbols-outlined text-sm text-[#c5a880]">menu_book</span>
                <span>Source: National Council on Aging</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ========================================== -->
      <!-- SECTION 3: THE REGENCY SAFEGUARD           -->
      <!-- ========================================== -->
      <section id="safeguard-protocols" class="py-24 px-6 sm:px-12 lg:px-24 bg-[#0e1726] text-white relative">
        <div class="max-w-7xl mx-auto">
          <!-- Section Header -->
          <div class="max-w-3xl mb-16 space-y-4">
            <span class="font-sans font-bold text-xs tracking-[0.25em] text-[#c5a880] uppercase">FOUR-POINT SAFETY MATRIX</span>
            <h2 class="font-serif text-3xl sm:text-4xl lg:text-5xl text-white font-normal">
              The Regency Safeguard: How We Protect Your Sanctuary
            </h2>
            <p class="font-sans text-base sm:text-lg text-stone-300 font-light leading-relaxed">
              Every cleaning appointment double-functions as a proactive safety inspection and hazard mitigation procedure.
            </p>
          </div>

          <!-- 4-Grid Feature Matrix -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <!-- Item 1 -->
            <div class="bg-stone-900/60 p-10 rounded-xl border border-[#c5a880]/30 backdrop-blur-sm space-y-6 hover:border-[#c5a880]/60 transition-all duration-300">
              <div class="flex items-center justify-between">
                <div class="w-14 h-14 rounded-full bg-[#c5a880]/15 flex items-center justify-center">
                  <span class="material-symbols-outlined text-3xl text-[#c5a880]">do_not_step</span>
                </div>
                <span class="px-3 py-1 bg-[#c5a880]/20 text-[#c5a880] font-sans font-bold text-[10px] tracking-widest uppercase rounded">
                  FALL MITIGATION
                </span>
              </div>
              <h3 class="font-serif text-2xl text-white font-normal">
                Slip & Fall Hazard Elimination
              </h3>
              <p class="font-sans text-stone-300 text-sm leading-relaxed font-light">
                We meticulously treat wet zones. Our technicians apply non-slip drying techniques to marble and hardwood, eliminate stray cord hazards, and ensure every walkway is completely unobstructed.
              </p>
            </div>

            <!-- Item 2 -->
            <div class="bg-stone-900/60 p-10 rounded-xl border border-[#c5a880]/30 backdrop-blur-sm space-y-6 hover:border-[#c5a880]/60 transition-all duration-300">
              <div class="flex items-center justify-between">
                <div class="w-14 h-14 rounded-full bg-[#c5a880]/15 flex items-center justify-center">
                  <span class="material-symbols-outlined text-3xl text-[#c5a880]">medical_services</span>
                </div>
                <span class="px-3 py-1 bg-[#c5a880]/20 text-[#c5a880] font-sans font-bold text-[10px] tracking-widest uppercase rounded">
                  MEDICAL SAFETY PROTOCOL
                </span>
              </div>
              <h3 class="font-serif text-2xl text-white font-normal">
                CPR & First Aid Certified Specialists
              </h3>
              <p class="font-sans text-stone-300 text-sm leading-relaxed font-light">
                Unlike standard cleaning crews, every Regency specialist is certified in First Aid and CPR, providing an essential layer of quiet vigilance and reassurance for your family.
              </p>
            </div>

            <!-- Item 3 -->
            <div class="bg-stone-900/60 p-10 rounded-xl border border-[#c5a880]/30 backdrop-blur-sm space-y-6 hover:border-[#c5a880]/60 transition-all duration-300">
              <div class="flex items-center justify-between">
                <div class="w-14 h-14 rounded-full bg-[#c5a880]/15 flex items-center justify-center">
                  <span class="material-symbols-outlined text-3xl text-[#c5a880]">accessibility_new</span>
                </div>
                <span class="px-3 py-1 bg-[#c5a880]/20 text-[#c5a880] font-sans font-bold text-[10px] tracking-widest uppercase rounded">
                  STRAIN PREVENTION
                </span>
              </div>
              <h3 class="font-serif text-2xl text-white font-normal">
                Zero Heavy Lifting or Reaching
              </h3>
              <p class="font-sans text-stone-300 text-sm leading-relaxed font-light">
                Never climb a stool or move furniture again. We handle heavy vacuuming, chandelier polishing, high-drapery dusting, and hard-to-reach detailing with professional equipment.
              </p>
            </div>

            <!-- Item 4 -->
            <div class="bg-stone-900/60 p-10 rounded-xl border border-[#c5a880]/30 backdrop-blur-sm space-y-6 hover:border-[#c5a880]/60 transition-all duration-300">
              <div class="flex items-center justify-between">
                <div class="w-14 h-14 rounded-full bg-[#c5a880]/15 flex items-center justify-center">
                  <span class="material-symbols-outlined text-3xl text-[#c5a880]">spa</span>
                </div>
                <span class="px-3 py-1 bg-[#c5a880]/20 text-[#c5a880] font-sans font-bold text-[10px] tracking-widest uppercase rounded">
                  WELLNESS-FIRST
                </span>
              </div>
              <h3 class="font-serif text-2xl text-white font-normal">
                Hypoallergenic & Eco-Safe Formulations
              </h3>
              <p class="font-sans text-stone-300 text-sm leading-relaxed font-light">
                We preserve indoor air quality using gentle, plant-derived, fragrance-free solutions that protect sensitive respiratory systems without compromising sanitation.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- ========================================== -->
      <!-- SECTION 4: DUAL AUDIENCE VALUE PROPOSITION -->
      <!-- ========================================== -->
      <section class="py-24 px-6 sm:px-12 lg:px-24 bg-[#f9f8f6]">
        <div class="max-w-7xl mx-auto">
          <!-- Section Header -->
          <div class="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span class="font-sans font-bold text-xs tracking-[0.25em] text-[#c5a880] uppercase">DESIGNED FOR RESIDENTS & FAMILIES</span>
            <h2 class="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0e1726] font-normal">
              Tailored Care. Uncompromised Respect.
            </h2>
          </div>

          <!-- Two Asymmetric Editorial Columns -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
            <!-- Column A: Resident -->
            <div class="bg-white p-12 rounded-2xl border border-[#c5a880]/30 shadow-sm flex flex-col justify-between space-y-8">
              <div class="space-y-6">
                <div class="inline-block px-3 py-1 bg-[#0e1726] text-[#c5a880] font-sans font-bold text-xs tracking-widest uppercase rounded">
                  FOR THE RESIDENT
                </div>
                <h3 class="font-serif text-3xl text-[#0e1726] font-normal">
                  An Uninterrupted Lifestyle
                </h3>
                <p class="font-sans text-stone-700 text-base leading-relaxed font-light">
                  Your home remains your personal haven. Our specialists arrive punctually, work with quiet etiquette, respect your preferred schedule, and care for your heirlooms and fine materials with expert precision.
                </p>

                <ul class="space-y-4 pt-4 border-t border-stone-100 font-sans text-sm text-stone-600">
                  <li class="flex items-start gap-3">
                    <span class="material-symbols-outlined text-[#c5a880] text-lg shrink-0 mt-0.5">check_circle</span>
                    <span>Quiet, non-disruptive white-glove service etiquette</span>
                  </li>
                  <li class="flex items-start gap-3">
                    <span class="material-symbols-outlined text-[#c5a880] text-lg shrink-0 mt-0.5">check_circle</span>
                    <span>Dedicated, familiar steward assigned to your residence</span>
                  </li>
                  <li class="flex items-start gap-3">
                    <span class="material-symbols-outlined text-[#c5a880] text-lg shrink-0 mt-0.5">check_circle</span>
                    <span>Careful handling of fine marble, antique wood, & heirloom crystal</span>
                  </li>
                </ul>
              </div>

              <div class="pt-6">
                <button 
                  (click)="scrollToSection('concierge-form')"
                  class="text-[#0e1726] font-sans font-bold text-sm hover:text-[#c5a880] transition-colors inline-flex items-center gap-2 cursor-pointer">
                  <span>Schedule Resident Walkthrough</span>
                  <span class="material-symbols-outlined text-base">arrow_forward</span>
                </button>
              </div>
            </div>

            <!-- Column B: Family Loved Ones -->
            <div class="bg-white p-12 rounded-2xl border border-[#c5a880]/30 shadow-sm flex flex-col justify-between space-y-8">
              <div class="space-y-6">
                <div class="inline-block px-3 py-1 bg-[#c5a880]/20 text-[#0e1726] font-sans font-bold text-xs tracking-widest uppercase rounded">
                  FOR LOVED ONES
                </div>
                <h3 class="font-serif text-3xl text-[#0e1726] font-normal">
                  Peace of Mind from Afar
                </h3>
                <p class="font-sans text-stone-700 text-base leading-relaxed font-light">
                  Ensure your parents’ living environment is safe, immaculate, and hazard-free without taking away their independence. Receive arrival confirmations, consistent dedicated personnel, and direct concierge access.
                </p>

                <ul class="space-y-4 pt-4 border-t border-stone-100 font-sans text-sm text-stone-600">
                  <li class="flex items-start gap-3">
                    <span class="material-symbols-outlined text-[#c5a880] text-lg shrink-0 mt-0.5">check_circle</span>
                    <span>Optional arrival and completion notifications for family</span>
                  </li>
                  <li class="flex items-start gap-3">
                    <span class="material-symbols-outlined text-[#c5a880] text-lg shrink-0 mt-0.5">check_circle</span>
                    <span>Background-checked, CPR-certified personnel</span>
                  </li>
                  <li class="flex items-start gap-3">
                    <span class="material-symbols-outlined text-[#c5a880] text-lg shrink-0 mt-0.5">check_circle</span>
                    <span>Direct concierge phone line for updates and schedule changes</span>
                  </li>
                </ul>
              </div>

              <div class="pt-6">
                <button 
                  (click)="scrollToSection('concierge-form')"
                  class="text-[#0e1726] font-sans font-bold text-sm hover:text-[#c5a880] transition-colors inline-flex items-center gap-2 cursor-pointer">
                  <span>Speak With Family Concierge</span>
                  <span class="material-symbols-outlined text-base">arrow_forward</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ========================================== -->
      <!-- SECTION 5: SOCIAL PROOF & TESTIMONIAL      -->
      <!-- ========================================== -->
      <section class="py-24 px-6 sm:px-12 lg:px-24 bg-[#0e1726] text-white relative overflow-hidden">
        <div class="max-w-4xl mx-auto text-center space-y-8 relative z-10">
          <div class="flex justify-center text-[#c5a880]">
            <span class="material-symbols-outlined text-5xl">format_quote</span>
          </div>

          <blockquote class="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-100 font-light leading-relaxed italic">
            "Regency has allowed my mother to remain comfortably in her Queens apartment. She retains all her independence, the home is immaculate, and knowing her specialists are CPR-certified gives our family true peace of mind."
          </blockquote>

          <div class="pt-4">
            <p class="font-sans font-medium text-base text-[#c5a880] uppercase tracking-widest">
              — Lydia K., Queens, NY
            </p>
            <p class="font-sans text-xs text-stone-400 mt-1">
              Jamaica  Queens Resident
            </p>
          </div>
        </div>
      </section>

      <!-- ========================================== -->
      <!-- SECTION 6: CONCIERGE CALL-TO-ACTION & FORM -->
      <!-- ========================================== -->
      <section id="concierge-form" class="py-24 px-6 sm:px-12 lg:px-24 bg-[#f9f8f6]">
        <div class="max-w-5xl mx-auto bg-white rounded-3xl border border-[#c5a880]/40 p-8 sm:p-14 shadow-xl">
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <!-- Left Info Panel -->
            <div class="lg:col-span-5 space-y-6">
              <span class="font-sans font-bold text-xs tracking-[0.25em] text-[#c5a880] uppercase">DISCREET INQUIRY</span>
              <h2 class="font-serif text-3xl sm:text-4xl text-[#0e1726] font-normal leading-tight">
                An Elevated Standard of In-Home Care.
              </h2>
              <p class="font-sans text-stone-600 text-sm leading-relaxed font-light">
                Schedule a discreet walkthrough with our client concierge to design a personalized maintenance schedule tailored to your residence.
              </p>

              <div class="pt-6 border-t border-stone-100 space-y-4">
                <div class="text-xs font-sans font-semibold uppercase tracking-wider text-stone-400">Direct Concierge Phone Line</div>
                <a href="tel:6463053661" class="inline-flex items-center gap-3 text-xl font-bold text-[#0e1726] hover:text-[#c5a880] transition-colors">
                  <span class="w-10 h-10 rounded-full bg-[#c5a880]/15 flex items-center justify-center text-[#c5a880]">
                    <span class="material-symbols-outlined text-xl">call</span>
                  </span>
                  <span>(646) 305-3661</span>
                </a>
              </div>
            </div>

            <!-- Right Form Panel -->
            <div class="lg:col-span-7">
              <form (ngSubmit)="submitForm()" class="space-y-5">
                @if (formSubmitted) {
                  <div class="p-6 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-900 space-y-3">
                    <div class="flex items-center gap-2 font-bold font-serif text-lg">
                      <span class="material-symbols-outlined text-emerald-600">check_circle</span>
                      <span>Consultation Request Received</span>
                    </div>
                    <p class="text-sm font-light">
                      Thank you, {{ formData.name }}. Our client concierge will reach out to you shortly at {{ formData.phone }} to confirm your private walkthrough.
                    </p>
                    <div class="pt-2 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <a 
                        routerLink="/book" 
                        [queryParams]="{ type: 'senior', track: 'walkthrough' }"
                        class="inline-flex items-center gap-2 text-xs font-sans font-bold text-[#0e1726] bg-[#c5a880] hover:bg-[#b0936b] px-4 py-2 rounded-lg transition-colors shadow-sm">
                        <span>Pick Exact Walkthrough Time on Calendar</span>
                        <span class="material-symbols-outlined text-xs">arrow_forward</span>
                      </a>
                      <button 
                        type="button" 
                        (click)="resetForm()"
                        class="text-xs font-sans font-semibold text-emerald-800 underline hover:text-emerald-950 cursor-pointer">
                        Arrange another consultation
                      </button>
                    </div>
                  </div>
                }

                @if (!formSubmitted) {
                  <div class="space-y-4">
                    <!-- Name Input -->
                    <div>
                      <label class="block font-sans text-xs font-semibold text-[#0e1726] uppercase tracking-wider mb-2">Full Name *</label>
                      <input 
                        type="text" 
                        required 
                        [(ngModel)]="formData.name" 
                        name="name"
                        placeholder="e.g. Eleanor Vance" 
                        class="w-full h-14 px-4 bg-[#f9f8f6] border border-stone-200 rounded-lg text-sm text-[#0e1726] focus:outline-none focus:border-[#c5a880] focus:ring-1 focus:ring-[#c5a880] transition-all"
                      />
                    </div>

                    <!-- Email & Phone Grid -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label class="block font-sans text-xs font-semibold text-[#0e1726] uppercase tracking-wider mb-2">Email Address *</label>
                        <input 
                          type="email" 
                          required 
                          [(ngModel)]="formData.email" 
                          name="email"
                          placeholder="eleanor@example.com" 
                          class="w-full h-14 px-4 bg-[#f9f8f6] border border-stone-200 rounded-lg text-sm text-[#0e1726] focus:outline-none focus:border-[#c5a880] focus:ring-1 focus:ring-[#c5a880] transition-all"
                        />
                      </div>
                      <div>
                        <label class="block font-sans text-xs font-semibold text-[#0e1726] uppercase tracking-wider mb-2">Phone Number *</label>
                        <input 
                          type="tel" 
                          required 
                          [(ngModel)]="formData.phone" 
                          name="phone"
                          placeholder="(646) 555-0199" 
                          class="w-full h-14 px-4 bg-[#f9f8f6] border border-stone-200 rounded-lg text-sm text-[#0e1726] focus:outline-none focus:border-[#c5a880] focus:ring-1 focus:ring-[#c5a880] transition-all"
                        />
                      </div>
                    </div>

                    <!-- Zip Code & Preferred Contact Time -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label class="block font-sans text-xs font-semibold text-[#0e1726] uppercase tracking-wider mb-2">Residence Zip Code *</label>
                        <input 
                          type="text" 
                          required 
                          [(ngModel)]="formData.zipCode" 
                          name="zipCode"
                          placeholder="10021" 
                          class="w-full h-14 px-4 bg-[#f9f8f6] border border-stone-200 rounded-lg text-sm text-[#0e1726] focus:outline-none focus:border-[#c5a880] focus:ring-1 focus:ring-[#c5a880] transition-all"
                        />
                      </div>
                      <div>
                        <label class="block font-sans text-xs font-semibold text-[#0e1726] uppercase tracking-wider mb-2">Preferred Contact Time</label>
                        <select 
                          [(ngModel)]="formData.preferredTime" 
                          name="preferredTime"
                          class="w-full h-14 px-4 bg-[#f9f8f6] border border-stone-200 rounded-lg text-sm text-[#0e1726] focus:outline-none focus:border-[#c5a880] focus:ring-1 focus:ring-[#c5a880] transition-all cursor-pointer">
                          <option value="Morning">Morning (9am - 12pm)</option>
                          <option value="Afternoon">Afternoon (12pm - 5pm)</option>
                          <option value="Evening">Evening (5pm - 8pm)</option>
                          <option value="Anytime">Anytime</option>
                        </select>
                      </div>
                    </div>

                    <!-- Submit Button -->
                    <div class="pt-2">
                      <button 
                        type="submit" 
                        [disabled]="isSubmitting"
                        class="w-full bg-[#0e1726] hover:bg-stone-900 text-[#c5a880] font-sans font-bold h-14 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer text-base disabled:opacity-50">
                        @if (!isSubmitting) {
                          <span>Arrange Your Consultation</span>
                        }
                        @if (isSubmitting) {
                          <span class="flex items-center gap-2">
                            <span class="w-4 h-4 border-2 border-[#c5a880] border-t-transparent rounded-full animate-spin"></span>
                            <span>Submitting Request...</span>
                          </span>
                        }
                      </button>
                    </div>
                  </div>
                }
              </form>
            </div>
          </div>
        </div>
      </section>

    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class SeniorIndependenceComponent {
  private readonly crm = inject(CrmService, { optional: true });
  private readonly affiliateTracking = inject(AffiliateTrackingService, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);

  formData = {
    name: '',
    email: '',
    phone: '',
    zipCode: '',
    preferredTime: 'Morning'
  };

  private readonly _isSubmitting = signal(false);
  private readonly _formSubmitted = signal(false);

  get isSubmitting(): boolean {
    return this._isSubmitting();
  }
  set isSubmitting(val: boolean) {
    this._isSubmitting.set(val);
  }

  get formSubmitted(): boolean {
    return this._formSubmitted();
  }
  set formSubmitted(val: boolean) {
    this._formSubmitted.set(val);
  }

  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  scrollToSection(id: string) {
    const element = document.getElementById(id);
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  resetForm() {
    this.formData = {
      name: '',
      email: '',
      phone: '',
      zipCode: '',
      preferredTime: 'Morning'
    };
    this.formSubmitted = false;
    this.isSubmitting = false;
    this.cdr.markForCheck();
  }

  async submitForm() {
    if (!this.formData.name || !this.formData.email || !this.formData.phone || !this.formData.zipCode) {
      return;
    }

    this.isSubmitting = true;
    this.cdr.markForCheck();

    // Read the active referral (slug, affiliate ID, and affiliate full name) from cookie/localStorage/service
    const activeRef = this.affiliateTracking?.getActiveReferral?.();
    const affiliateSlug = activeRef?.slug || (typeof document !== 'undefined' ? (this.getCookie('rhc_affiliate_ref') || localStorage.getItem('rhc_affiliate_ref')) : null);
    const affiliateId = activeRef?.affiliateId || (typeof localStorage !== 'undefined' ? localStorage.getItem('rhc_affiliate_id') : null);
    const affiliateName = activeRef?.affiliateName || (typeof document !== 'undefined' ? (this.getCookie('rhc_affiliate_name') || localStorage.getItem('rhc_affiliate_name')) : null);

    const leadPayload: Record<string, any> = {
      firstName: this.formData.name.split(' ')[0] || this.formData.name,
      lastName: this.formData.name.split(' ').slice(1).join(' ') || 'Resident',
      email: this.formData.email,
      phone: this.formData.phone,
      zipCode: this.formData.zipCode,
      zip: this.formData.zipCode,
      preferredTime: this.formData.preferredTime,
      leadSource: 'Senior Independence Consultation',
      message: `Senior Independence Inquiry. Zip: ${this.formData.zipCode}. Preferred Time: ${this.formData.preferredTime}`,
      notes: `Senior Independence Inquiry. Zip: ${this.formData.zipCode}. Preferred Time: ${this.formData.preferredTime}`,
      ...(affiliateSlug ? { affiliateSlug } : {}),
      ...(affiliateId ? { affiliateId } : {}),
      ...(affiliateName ? { affiliateName } : {}),
    };

    const dealPayload: Record<string, any> = {
      dealName: `Senior Independence - ${this.formData.name}`,
      stage: '1. New Inquiry / Discovery',
      pipeline: 'Senior Residential Cleaning',
      decisionMakerPersona: 'Senior Resident',
      preferredQuoteTrack: 'Track B - In-Home Safety Walkthrough',
      preferredContactTime: this.formData.preferredTime,
      sponsorName: this.formData.name,
      seniorResidentName: this.formData.name,
      contactEmail: this.formData.email,
      contactPhone: this.formData.phone,
      serviceAddress: `Zip: ${this.formData.zipCode}`,
      zip: this.formData.zipCode,
      leadSource: 'Senior Independence Consultation Form',
      notes: `Inquiry submitted via Senior Independence page. Preferred contact time: ${this.formData.preferredTime}.`,
      ...(affiliateSlug ? { affiliateSlug } : {}),
      ...(affiliateId ? { affiliateId } : {}),
      ...(affiliateName ? { affiliateName } : {})
    };

    try {
      if (this.crm) {
        await Promise.allSettled([
          firstValueFrom(this.crm.requestConsultation(leadPayload)),
          firstValueFrom(this.crm.createDeal(dealPayload))
        ]);
      } else if (typeof fetch === 'function') {
        const baseUrl = environment?.apiBaseUrl || '';
        await Promise.allSettled([
          fetch(`${baseUrl}/api/crm/leads`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(leadPayload)
          }),
          fetch(`${baseUrl}/api/crm/deals`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dealPayload)
          })
        ]);
      }
    } catch (err) {
      console.warn('[SeniorIndependence] Form submission API fallback:', err);
    } finally {
      this.isSubmitting = false;
      this.formSubmitted = true;
      this.cdr.markForCheck();
      try {
        this.cdr.detectChanges();
      } catch {
        // Safe fallback if called during an active tick
      }
    }
  }
}
